import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { EnterpriseDto } from '../../common/interfaces/enterprise-dto';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, Observable, of, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { GroupSearchService } from '../../common/services/group-search.service';

@Component({
    selector: 'lib-audit-report-enterprise-select',
    imports: [
        AsyncPipe,
        NgIf,
        NgLabelTemplateDirective,
        NgOptionTemplateDirective,
        NgSelectComponent,
        FormsModule,
        NgClass
    ],
    templateUrl: './audit-report-enterprise-select.component.html',
    styleUrl: './audit-report-enterprise-select.component.scss'
})
export class AuditReportEnterpriseSelectComponent implements OnInit, OnDestroy {
    private readonly groupSearchService = inject(GroupSearchService);
    private readonly toastrService = inject(ToastrService);

    @Input() enterpriseId!: number;
    @Input() placeholder = 'Enterprise Search...';
    @Input() multiple = false;
    @Output() enterprisesSelectedEvent = new EventEmitter<EnterpriseDto[] | EnterpriseDto>();
    @ViewChild(NgSelectComponent, { static: true }) ngSelect!: NgSelectComponent;

    currentSearchTerm = '';
    selectedEnterprises: EnterpriseDto[] | EnterpriseDto | undefined;
    enterprisesSearchInput$ = new Subject<string>();
    enterprises$: Observable<(EnterpriseDto)[]> = of([]);
    enterprisesLoading = false;

    protected destroyed$: Subject<void> = new Subject<void>();

    ngOnInit() {
        this.loadEnterprises();
        outputToObservable(this.ngSelect.changeEvent)
            .pipe(takeUntil(this.destroyed$))
            .subscribe($e => this.onValueChange($e));
    }

    loadEnterprises(): void {
        const excludedIds = Array.isArray(this.selectedEnterprises) && this.multiple
            ? this.selectedEnterprises.map(u => u.id)
            : [];

        this.enterprises$ = this.enterprisesSearchInput$.pipe(
            startWith(''),
            filter(searchTerm => searchTerm?.length === 0 || searchTerm?.length >= 3),
            distinctUntilChanged(),
            debounceTime(200),
            tap(() => this.enterprisesLoading = this.multiple),
            switchMap(searchTerm => this.groupSearchService.getGroups(searchTerm, 0, 5, 'name', 'ASC')
                .pipe(
                    map(response => {
                        return response.content.filter(ent => ent?.id !== null && !excludedIds?.includes(ent?.id));
                    }),
                    catchError(() => {
                        this.toastrService.error('Failed to retrieve enterprises.');
                        return of([]);
                    }),
                    finalize(() => this.enterprisesLoading = false)
                ))
        );
    }

    onValueChange(value: EnterpriseDto | EnterpriseDto[]): void {
        this.ngSelect.close();
        this.enterprisesSelectedEvent.emit(value);
    }

    resetCurrentSearch(): void {
        this.selectedEnterprises = this.multiple ? [] : undefined;
        this.enterprisesSelectedEvent.emit(this.selectedEnterprises);
        this.enterprisesSearchInput$.next('');
        this.ngSelect.close();
    }

    onSearchTermChange(event: { term: string; items: EnterpriseDto[] }): void {
        if (this.currentSearchTerm !== event.term) {
            this.currentSearchTerm = event.term;
            if (!this.multiple) {
                this.selectedEnterprises = undefined;
            }
            this.enterprisesSearchInput$.next(event.term);
        }
    }

    onDropdownOpen() {
        this.enterprisesSearchInput$.next('');
    }

    onDropdownClose() {
        if (this.selectedEnterprises === undefined) {
            this.resetCurrentSearch();
        }
    }

    onClear(item: Partial<EnterpriseDto>): void {
        this.ngSelect.clearItem(item);
    }

    compareWith(input: EnterpriseDto, selected: EnterpriseDto): boolean {
        return input.id === selected.id;
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}

